import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * <BackOrHomeButton /> — site-wide standard back/home navigation.
 *
 * THE PATTERN (now a PRD standing rule — see top of /app/memory/PRD.md):
 *
 *   * If the user navigated INTO this page from somewhere else in
 *     DiviDrip (via <Link>, navigate(), a chip click, a modal CTA,
 *     etc.) → render ⬅️ "Back" and call navigate(-1) on click.
 *
 *   * If the user landed COLD (deep link, shared URL, browser refresh,
 *     external Google/Reddit/X click) → render 🏠 "Home" and call
 *     navigate('/') on click.
 *
 * Why `location.key`?
 *   `document.referrer` is unreliable because React Router does
 *   client-side navigation (no full page load → referrer never
 *   updates). `location.key` is literally the string 'default' only
 *   for the very first entry of a browser session, and a unique
 *   opaque id for every subsequent in-app navigation. That makes it
 *   the canonical "came from inside the app" signal — independent of
 *   how the navigation happened.
 *
 * Esc keyboard shortcut:
 *   In `back` mode only, pressing Esc fires navigate(-1) just like
 *   the button click. On `home` mode the binding is intentionally
 *   not registered — a cold visitor pressing Esc on a shared link
 *   should not boomerang off the page.
 *
 * Props:
 *   * `showEscHint` (bool, default true) — shows the "Press Esc to go
 *     back" pill on desktop (hidden < sm:). Pages with a compact
 *     header can pass false to suppress.
 *   * `escDisabled` (bool, default false) — opt-out of the Esc
 *     keyboard binding entirely. Useful when a modal/dialog on the
 *     page already owns Esc (e.g. share-card modal). Pages should
 *     pass `escDisabled={shareOpen || modalOpen}` to defer cleanly.
 *   * `testid` (string) — overrides the default data-testid so each
 *     page's button still has a stable, page-scoped selector.
 *   * `className` — passthrough for spacing/positioning tweaks.
 *   * `size` — Button size variant (default "sm").
 */
export default function BackOrHomeButton({
  showEscHint = true,
  escDisabled = false,
  testid = 'back-or-home-btn',
  className = '',
  size = 'sm',
  escHintStacked = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Lock the mode at mount-time so subsequent in-page state changes
  // (e.g. modal opens that touch the history) don't flip "Back" into
  // "Home" mid-session.
  const [mode] = useState(() =>
    (location.key && location.key !== 'default') ? 'back' : 'home'
  );

  // ──────────────────────────────────────────────────────────────────
  // Round 78 — same-page hop skipping (Feb 12, 2026, refined Round 78b)
  // ──────────────────────────────────────────────────────────────────
  // User-reported bug: on long pages like /glossary the user clicks
  // multiple intra-page anchors (`#letter-A`, `#letter-B`, "Back to
  // top", abbreviation tooltip links). Each anchor click pushes a
  // history entry with the SAME pathname but a different `location.key`
  // and `location.hash`. The vanilla `navigate(-1)` then walks the
  // user through each hop instead of returning to the PREVIOUS PAGE.
  //
  // Round 78a tried tracking `location.key` changes via `useLocation`,
  // but React Router's BrowserRouter doesn't see raw `<a href="#...">`
  // anchor clicks (those fire `hashchange`, not `popstate`). The
  // Glossary alphabet bar uses exactly those raw anchors.
  //
  // Round 78b — listen DIRECTLY to the two browser events that push
  // history entries while staying on the same pathname:
  //   * `hashchange` — fires on any URL fragment change (the anchor
  //     case that broke Round 78a)
  //   * `popstate` — fires on browser back/forward + on manual
  //     `history.pushState` calls
  // We count both while `window.location.pathname` matches the
  // mount-time pathname, and the back click then navigates back by
  // exactly that many entries to collapse the entire intra-page
  // history into a single "back" jump.
  // ──────────────────────────────────────────────────────────────────
  // Track every unique URL (pathname + hash + search) we've seen on
  // this page since mount. Set semantics deduplicate the standard
  // Chromium quirk where one anchor click fires BOTH hashchange AND
  // popstate. Counter = `size` of the Set.
  const originPathRef = useRef(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const urlsSeenRef = useRef(
    new Set(typeof window !== 'undefined'
      ? [window.location.pathname + window.location.search + window.location.hash]
      : ['/'])
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHop = () => {
      if (window.location.pathname !== originPathRef.current) {
        originPathRef.current = window.location.pathname;
        urlsSeenRef.current = new Set([
          window.location.pathname + window.location.search + window.location.hash,
        ]);
        return;
      }
      urlsSeenRef.current.add(
        window.location.pathname + window.location.search + window.location.hash
      );
    };
    window.addEventListener('hashchange', onHop);
    window.addEventListener('popstate', onHop);
    return () => {
      window.removeEventListener('hashchange', onHop);
      window.removeEventListener('popstate', onHop);
    };
  }, []);

  const handleClick = () => {
    if (mode === 'home') {
      navigate('/dashboard');
      return;
    }
    // Round 79 — StockModal back-restore. When the page we landed on
    // was opened via an in-modal navigation, `<BackOrHomeButton />`
    // honors `history.state.usr.modalOrigin` and jumps DIRECTLY to
    // the origin page WITH a `reopenModalTicker` flag the origin
    // page reads via `useReopenModalOnMount()`. This bypasses both
    // the regular `navigate(-N)` AND the hop-skip path — exactly one
    // step, even if intra-page anchors were clicked.
    try {
      const usrState = (typeof window !== 'undefined' && window.history && window.history.state)
        ? window.history.state.usr
        : null;
      if (usrState && usrState.modalOrigin && usrState.modalOrigin.fromPath) {
        const { fromPath, ticker, tab } = usrState.modalOrigin;
        navigate(fromPath, {
          state: { reopenModalTicker: ticker, reopenModalTab: tab || null },
        });
        return;
      }
    } catch {
      // Fall through to the normal back path on any read error.
    }
    // Default: collapse any intra-page anchor hops on this pathname
    // into a single back step. See Round 78b notes above.
    const wanted = urlsSeenRef.current.size;
    let safe = wanted;
    if (typeof window !== 'undefined') {
      const maxSafe = Math.max(1, window.history.length - 1);
      safe = Math.min(wanted, maxSafe);
    }
    navigate(-safe);
  };

  useEffect(() => {
    if (mode !== 'back' || escDisabled) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Only intercept when no other element/dialog has taken focus
        // for keyboard input. Modals usually portal outside the page's
        // root and capture Esc themselves before this fires.
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        // Sep 2026 — an open Radix dialog/popover owns Esc (it closes
        // itself); don't also navigate away underneath it.
        if (e.defaultPrevented || document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
        e.preventDefault();
        handleClick();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // handleClick is stable — references refs + navigate which is also stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, escDisabled, navigate]);

  return (
    <div className={`inline-flex items-center gap-3 ${className}`} data-testid={`${testid}-wrap`}>
      <Button
        variant="ghost"
        size={size}
        onClick={handleClick}
        data-testid={testid}
      >
        {mode === 'back' ? (
          <><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</>
        ) : (
          <><Home className="w-4 h-4 mr-1.5" /> Home</>
        )}
      </Button>
      {mode === 'back' && showEscHint && (
        escHintStacked ? (
          <span
            className="hidden sm:inline-flex flex-col items-start leading-tight text-[10px] text-muted-foreground/70"
            data-testid={`${testid}-esc-hint`}
          >
            <span className="inline-flex items-center gap-1">
              Press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">Esc</kbd>
            </span>
            <span>to go back</span>
          </span>
        ) : (
          <span
            className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground/70"
            data-testid={`${testid}-esc-hint`}
          >
            Press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">Esc</kbd> to go back
          </span>
        )
      )}
    </div>
  );
}
